/**
 * GMA-1617 (CompassCare Web 2/14 — Overview) — KPI strip.
 *
 * The Overview's row of KPI tiles. Each tile is ALSO a filter: clicking one
 * scopes the client grid to that tile's `filter` (a CLIENT_FILTERS key). This
 * component is presentational — it renders KpiTiles from an injected `kpis`
 * descriptor array and reports the chosen filter via `onSelectFilter`; deriving
 * the descriptors from live data (counts + filter mapping) is `computeKpis`'s
 * job (a separate model concern), so this stays trivially testable.
 *
 * A tile is interactive only when it carries a `filter` AND `onSelectFilter` is
 * provided; purely-informational tiles (no filter) render as non-interactive.
 * a11y: a labelled group; each tile is the KpiTile primitive (button + aria).
 * Fail-safe: a null/empty `kpis` renders an empty (but labelled) group.
 */
import React from "react";
import PropTypes from "prop-types";
import KpiTile from "./KpiTile";
import { theme } from "../../styles/designTokens";

export default function KpiStrip({ kpis, activeFilter, onSelectFilter }) {
  const tiles = Array.isArray(kpis) ? kpis.filter(Boolean) : [];
  return (
    <div
      data-testid="kpi-strip"
      role="group"
      aria-label="Key metrics"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        fontFamily: theme.fontUI,
      }}
    >
      {tiles.map((kpi) => {
        // A tile filters the grid only if it declares a filter and a handler
        // exists; otherwise it's informational (KpiTile renders it disabled).
        const interactive =
          typeof kpi.filter === "string" && typeof onSelectFilter === "function";
        return (
          <KpiTile
            key={kpi.key}
            label={kpi.label}
            value={kpi.value}
            unit={kpi.unit}
            severity={kpi.severity}
            active={typeof kpi.filter === "string" && kpi.filter === activeFilter}
            onSelect={interactive ? () => onSelectFilter(kpi.filter) : undefined}
          />
        );
      })}
    </div>
  );
}

KpiStrip.propTypes = {
  /** KPI tile descriptors: { key, label, value, unit?, severity?, filter? }. */
  kpis: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit: PropTypes.string,
      severity: PropTypes.string,
      /** A CLIENT_FILTERS key this tile scopes the grid to; null = informational. */
      filter: PropTypes.string,
    }),
  ),
  /** The currently-applied filter key (marks the matching tile active). */
  activeFilter: PropTypes.string,
  /** Called with a tile's filter key when an interactive tile is clicked. */
  onSelectFilter: PropTypes.func,
};
